/**
 * useVisitDay — the rep's clock-in/out leg chain, IDB-backed (#3452).
 *
 * The hook owns an append-only list of legs (the model lives in
 * `utils/dayLegs`): any number of clock-in/out windows roll up into one logical
 * day, and the `dayData` every consumer reads is derived from that list.
 *
 * Storage: one row per rep-local day, keyed `YYYYMMDD` (`rowIdForDayKey`).
 *   - A day with an OPEN clock leg is resumed regardless of the calendar date,
 *     so a session running past midnight (triage #22) or a punch the rep forgot
 *     to close survives to be corrected rather than silently deleted.
 *   - A CLOSED day from an earlier date is left on disk (pruned after a week)
 *     and today starts empty.
 *   - Single-session legacy payloads (IDB id=1, or the older `rdm_day_tracking`
 *     localStorage blob) are converted to a leg chain on first load.
 *
 * When local has no day at all we rehydrate the rep's active day from the
 * server (#3354) so a fresh device / reinstall / impersonation can resume it.
 * Rehydrate-on-empty is the safe subset — nothing local to clobber.
 *
 * Callbacks mutate through `legsRef`, updated synchronously alongside React
 * state, so back-to-back calls (startDay → startDriving) see the real current
 * chain. Guards are therefore expressed against the legs themselves: an open
 * leg of the same type makes each opener idempotent.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { STORES, clear as clearStore, getAll, put, remove } from '../utils/indexedDB';
import { emit as emitEvent, setLastKnownLocation } from '../utils/eventLog';
import { dayApi, telemetryApi } from '../services/api';
import type { TelemetryEventType } from '../services/api';
import {
  activityLegs,
  clockLegs,
  closeLegs,
  closedDriveDataFrom,
  dayKeyOf,
  daysBetweenKeys,
  deriveDayData,
  elapsedMinutesFor,
  generateLegId,
  enclosingStartFor,
  hasOpenClock,
  legsFromDayData,
  openLegOfType,
  reviveDayData,
  reviveLegs,
  rowIdForDayKey,
  spanMinutes,
} from '../utils/dayLegs';
import type { ClosedDriveData, DayData, Location, TimeEntry } from '../utils/dayLegs';

export type { DayStatus, ActivityType, LegType, TimeEntry, ClosedDriveData } from '../utils/dayLegs';

// Legacy localStorage key — read once on mount to migrate users who had an
// active day before the IDB cutover (#1471).
const LEGACY_STORAGE_KEY = 'rdm_day_tracking';

// Pre-#3452 IDB singleton row id.
const LEGACY_SINGLETON_ID = 1;

// Closed day rows older than this are pruned on load. Only a housekeeping
// bound — nothing reads them; the Time page reads the server.
const KEEP_CLOSED_DAYS = 7;

interface StoredDayRow {
  id: number;
  version: number;
  dayKey: string;
  legs: TimeEntry[];
  currentLocation: Location | null;
}

interface LoadedDay {
  dayKey: string;
  legs: TimeEntry[];
  currentLocation: Location | null;
}

const ROW_VERSION = 2;

async function saveDay(dayKey: string, legs: TimeEntry[], currentLocation: Location | null): Promise<void> {
  await put<StoredDayRow>(STORES.VISIT_DAY_SESSIONS, {
    id: rowIdForDayKey(dayKey),
    version: ROW_VERSION,
    dayKey,
    legs,
    currentLocation,
  });
}

function reviveRow(raw: StoredDayRow): LoadedDay {
  return {
    dayKey: raw.dayKey,
    legs: reviveLegs(raw.legs),
    currentLocation: raw.currentLocation
      ? { ...raw.currentLocation, timestamp: new Date(raw.currentLocation.timestamp) }
      : null,
  };
}

/** Convert a single-session `DayData` payload into a leg chain on its own day row. */
function loadedFromDayData(day: DayData): LoadedDay | null {
  const legs = legsFromDayData(day);
  if (!legs.length || !day.startTime) return null;
  return { dayKey: dayKeyOf(day.startTime), legs, currentLocation: day.currentLocation };
}

async function migrateLegacySingleton(row: Record<string, unknown>): Promise<LoadedDay | null> {
  try {
    const loaded = loadedFromDayData(reviveDayData(row));
    if (loaded) await saveDay(loaded.dayKey, loaded.legs, loaded.currentLocation);
    await remove(STORES.VISIT_DAY_SESSIONS, (row.id as number) ?? LEGACY_SINGLETON_ID);
    return loaded;
  } catch (err) {
    console.warn('[useVisitDay] legacy singleton migration failed:', err);
    return null;
  }
}

async function migrateFromLocalStorage(): Promise<LoadedDay | null> {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;
    const loaded = loadedFromDayData(reviveDayData(JSON.parse(raw) as Record<string, unknown>));
    if (loaded) await saveDay(loaded.dayKey, loaded.legs, loaded.currentLocation);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    return loaded;
  } catch (err) {
    console.warn('[useVisitDay] legacy localStorage migration failed:', err);
    return null;
  }
}

/**
 * Pick the day to resume: an open clock window first (most recent, whatever
 * its date), otherwise today's row. Prunes stale closed rows on the way.
 */
export async function loadCurrentDay(todayKey: string): Promise<LoadedDay | null> {
  const rows = await getAll<StoredDayRow>(STORES.VISIT_DAY_SESSIONS);
  const days: LoadedDay[] = [];

  for (const row of rows) {
    if (row.version !== ROW_VERSION) {
      // Single-session legacy row (or anything unrecognised) — convert once.
      const migrated = await migrateLegacySingleton(row as unknown as Record<string, unknown>);
      if (migrated) days.push(migrated);
      continue;
    }
    days.push(reviveRow(row));
  }

  if (!days.length) return migrateFromLocalStorage();

  days.sort((a, b) => (a.dayKey < b.dayKey ? 1 : a.dayKey > b.dayKey ? -1 : 0));
  const open = days.find((d) => hasOpenClock(d.legs));

  // Housekeeping: closed rows older than a week can go.
  await Promise.all(
    days
      .filter((d) => !hasOpenClock(d.legs) && daysBetweenKeys(todayKey, d.dayKey) > KEEP_CLOSED_DAYS)
      .map((d) => remove(STORES.VISIT_DAY_SESSIONS, rowIdForDayKey(d.dayKey)).catch(() => undefined))
  );

  return open ?? days.find((d) => d.dayKey === todayKey) ?? null;
}

async function sendTelemetry(
  eventType: TelemetryEventType,
  location?: Location | null,
  storeId?: number,
  accuracy?: number
) {
  try {
    await telemetryApi.recordEvent({
      event_type: eventType,
      event_timestamp: new Date().toISOString(),
      lat: location?.lat,
      lon: location?.lng,
      // Round to the server DecimalField precision; raw floats trip max_digits (#2958).
      gps_accuracy: accuracy != null ? Math.round(accuracy * 100) / 100 : accuracy,
      store_id: storeId,
      device_info: {
        os: navigator.userAgent.includes('iPhone') ? 'iOS' : navigator.userAgent.includes('Android') ? 'Android' : 'Web',
        app_version: (window as unknown as { __APP_VERSION__?: string }).__APP_VERSION__ || 'unknown',
      },
    });
  } catch (err) {
    console.warn('Telemetry event failed:', eventType, err);
  }
}

export interface UseVisitDayOptions {
  /**
   * The rep's most recent odometer reading. A leg stamps it as it opens and as
   * it closes, so drive mileage comes from the odometer the app already
   * collects (at clock-in and at each store arrival) with no extra prompt —
   * the vehicle doesn't move while the rep is inside a store, so the arrival
   * reading is also the departure reading.
   */
  currentOdometer?: number | null;
}

export function useVisitDay(options: UseVisitDayOptions = {}) {
  const [legs, setLegs] = useState<TimeEntry[]>([]);
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  const legsRef = useRef<TimeEntry[]>([]);
  const locationRef = useRef<Location | null>(null);
  const dayKeyRef = useRef<string | null>(null);
  // Mirrored in a ref so the punch callbacks read the reading current at the
  // moment of the punch rather than the one captured when they were created.
  const odometerRef = useRef<number | null>(null);
  odometerRef.current = options.currentOdometer ?? null;

  const dayData = useMemo(() => deriveDayData(legs, currentLocation), [legs, currentLocation]);

  /**
   * Single write path. Updates the refs synchronously so a follow-up callback
   * in the same tick sees the new chain, then schedules the React re-render.
   */
  const commit = useCallback((nextLegs: TimeEntry[], location?: Location | null) => {
    legsRef.current = nextLegs;
    if (!dayKeyRef.current && nextLegs.length) {
      dayKeyRef.current = dayKeyOf(nextLegs[0].startTime);
    }
    setLegs(nextLegs);
    if (location !== undefined && location !== null) {
      locationRef.current = location;
      setCurrentLocation(location);
    }
  }, []);

  /** Record which emitted punch opened or closed a leg. */
  const stampEventId = useCallback(
    (legId: string, field: 'startEventId' | 'endEventId', eventId: string) => {
      legsRef.current = legsRef.current.map((l) => (l.id === legId ? { ...l, [field]: eventId } : l));
      setLegs(legsRef.current);
    },
    []
  );

  const adopt = useCallback((loaded: LoadedDay) => {
    dayKeyRef.current = loaded.dayKey;
    legsRef.current = loaded.legs;
    locationRef.current = loaded.currentLocation;
    setLegs(loaded.legs);
    setCurrentLocation(loaded.currentLocation);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const restored = await loadCurrentDay(dayKeyOf(new Date()));
        if (cancelled) return;
        if (restored && restored.legs.length) {
          adopt(restored);
        } else if (navigator.onLine) {
          // Local is empty — ask the server for the rep's current active day.
          // Best-effort: any failure just leaves the day not_started so the
          // app still starts. Only 'active' is adopted (an ended day has no
          // work to continue; the timesheet already shows completed visits).
          try {
            const resp = await dayApi.getActiveDay();
            if (cancelled) return;
            const serverDay = resp?.day;
            if (serverDay && serverDay.status === 'active') {
              const loaded = loadedFromDayData(reviveDayData(serverDay as unknown as Record<string, unknown>));
              if (loaded) adopt(loaded);
            }
          } catch (err) {
            console.warn('[useVisitDay] active-day rehydrate failed (continuing local):', err);
          }
        }
      } catch (err) {
        console.error('[useVisitDay] failed to load day data:', err);
      } finally {
        if (!cancelled) setIsHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adopt]);

  // Persist the current day row whenever the chain changes (post-hydration).
  useEffect(() => {
    if (!isHydrated || !legs.length) return;
    const dayKey = dayKeyRef.current ?? dayKeyOf(legs[0].startTime);
    dayKeyRef.current = dayKey;
    void saveDay(dayKey, legs, currentLocation);
  }, [legs, currentLocation, isHydrated]);

  const getCurrentLocation = useCallback((): Promise<Location> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc: Location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            // Capture accuracy so the check-in write op can persist
            // tbl_CallLogNew.GPSAccuracy (#2817 / diag #62). Without this the
            // Location carried only lat/lng and every regular check-in stored
            // GPSAccuracy=NULL, leaving a coarse/far fix un-triageable.
            accuracy: position.coords.accuracy,
            timestamp: new Date(),
          };
          // Push into the shared event-log location cache so events
          // without GPS directly in scope (task_started, survey_submitted,
          // etc.) automatically carry a "rep was approximately here"
          // payload via mergeLocationIntoPayload(). (#1950 telemetry)
          setLastKnownLocation(loc.lat, loc.lng);
          resolve(loc);
        },
        (err) => reject(new Error(`GPS error: ${err.message}`)),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  }, []);

  const tryLocation = useCallback(async (): Promise<Location | null> => {
    try {
      return await getCurrentLocation();
    } catch {
      return null;
    }
  }, [getCurrentLocation]);

  const startDay = useCallback(
    async (retroactiveStartTime?: Date, options?: { openDrive?: boolean }) => {
      // Already inside a clock window — a second Start Day would open a
      // duplicate session (and a duplicate day_started on the ledger).
      if (hasOpenClock(legsRef.current)) return;
      setIsLoading(true);
      setError(null);
      const startTime = retroactiveStartTime || new Date();
      // The app can sit mounted overnight. Clocking in on a NEW calendar day
      // starts that day's chain rather than appending to yesterday's, which
      // would carry yesterday's legs, totals and start time into today.
      if (legsRef.current.length && dayKeyRef.current && dayKeyOf(startTime) !== dayKeyRef.current) {
        dayKeyRef.current = null;
        legsRef.current = [];
      }
      // A normal clock-in opens the outbound drive leg in the SAME write that
      // opens the clock leg, so the drive record for clock-in → first check-in
      // always exists. A retroactive start stays idle — its drive comes from
      // the backfill form — and so does a clock-in raised at a store the rep
      // has already reached, where there is no drive left to time.
      const beginDriving = options?.openDrive ?? !retroactiveStartTime;
      let resolvedLocation: Location | null = null;
      try {
        resolvedLocation = await getCurrentLocation();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to get location');
      }
      const clockLeg: TimeEntry = {
        id: generateLegId(),
        type: 'clock',
        startTime,
        startLocation: retroactiveStartTime ? undefined : resolvedLocation ?? undefined,
        manual: !!retroactiveStartTime,
      };
      const driveLeg: TimeEntry[] = beginDriving
        ? [{
            id: generateLegId(),
            type: 'drive',
            startTime,
            startLocation: resolvedLocation ?? undefined,
            startOdometer: odometerRef.current ?? undefined,
          }]
        : [];
      commit([...legsRef.current, clockLeg, ...driveLeg], resolvedLocation);
      sendTelemetry('start_day', resolvedLocation);
      setIsLoading(false);
      // Emit AFTER the state mutation succeeds. Best-effort — emit()
      // logs + swallows on IDB failure so a queue glitch can never
      // block the rep from actually starting their day. (#1950)
      void emitEvent({
        event_type: 'day_started',
        event_at: startTime,
        payload: {
          retroactive: !!retroactiveStartTime,
          lat: resolvedLocation?.lat ?? null,
          lon: resolvedLocation?.lng ?? null,
        },
      }).then((rec) => {
        // Remember the punch so a later correction can supersede it.
        if (rec) stampEventId(clockLeg.id, 'startEventId', rec.client_event_id);
      });
    },
    [commit, getCurrentLocation]
  );

  const endDay = useCallback(async () => {
    if (!hasOpenClock(legsRef.current)) return;
    setIsLoading(true);
    setError(null);
    const location = await tryLocation();
    const now = new Date();
    // Close every open leg — the activity legs first so the clock window
    // always encloses them.
    const next = closeLegs(legsRef.current, () => true, now, location, odometerRef.current);
    commit(next, location);
    const derived = deriveDayData(next, location ?? locationRef.current);
    const closedClock = clockLegs(next).find((l) => l.endTime?.getTime() === now.getTime());
    sendTelemetry('end_day', location);
    void emitEvent({
      event_type: 'day_ended',
      event_at: now,
      payload: {
        lat: location?.lat ?? null,
        lon: location?.lng ?? null,
        total_drive_minutes: derived.totalDriveMinutes,
        total_store_minutes: derived.totalStoreMinutes,
        total_miles: derived.totalMiles,
        time_entry_count: derived.timeEntries.length,
      },
    }).then((rec) => {
      if (rec && closedClock) stampEventId(closedClock.id, 'endEventId', rec.client_event_id);
    });
    setIsLoading(false);
  }, [commit, tryLocation]);

  const startDriving = useCallback(async () => {
    const current = legsRef.current;
    // Not clocked in, already driving, or still inside a store → nothing to do.
    if (!hasOpenClock(current)) return;
    if (openLegOfType(current, 'drive') || openLegOfType(current, 'store')) return;
    const location = await tryLocation();
    const now = new Date();
    commit(
      [
        ...legsRef.current,
        {
          id: generateLegId(),
          type: 'drive',
          startTime: now,
          startLocation: location ?? undefined,
          startOdometer: odometerRef.current ?? undefined,
        },
      ],
      location
    );
    void emitEvent({
      event_type: 'drive_started',
      event_at: now,
      payload: {
        lat: location?.lat ?? null,
        lon: location?.lng ?? null,
      },
    });
  }, [commit, tryLocation]);

  const stopDriving = useCallback(async (): Promise<ClosedDriveData | undefined> => {
    if (!hasOpenClock(legsRef.current)) return undefined;
    if (!openLegOfType(legsRef.current, 'drive')) return undefined;
    const location = await tryLocation();
    const now = new Date();
    const closed = closeLegs(legsRef.current, (l) => l.type === 'drive', now, location, odometerRef.current);
    const closedDriveData = closedDriveDataFrom(
      closed.find((l) => l.id === openLegOfType(legsRef.current, 'drive')?.id),
      now
    );
    commit(closed, location);
    sendTelemetry('stop_driving', location);
    void emitEvent({
      event_type: 'drive_ended',
      event_at: now,
      payload: {
        lat: location?.lat ?? null,
        lon: location?.lng ?? null,
        duration_minutes: closedDriveData?.durationMinutes ?? null,
        start_odometer: closedDriveData?.startOdometer ?? null,
        end_odometer: closedDriveData?.endOdometer ?? null,
      },
    });
    return closedDriveData;
  }, [commit, tryLocation]);

  const checkIntoStore = useCallback(
    async (storeId: number, storeName: string, projectId?: number): Promise<ClosedDriveData | undefined> => {
      // A rep who reaches a store without an open clock window used to get
      // NOTHING from this call — it returned early, so no store leg was
      // created, no drive was closed, and the check-in went on to write a
      // CallLog with no drive segment while the day model sat out entirely.
      // On prod that was 30 of 38 check-ins (#3519): reps reach their first
      // store before they think to clock in. The day is opened for them here
      // rather than lost, and the punch is theirs to correct on the Time page.
      const autoClockAt = hasOpenClock(legsRef.current)
        ? null
        : enclosingStartFor(legsRef.current, new Date());
      const openStore = openLegOfType(legsRef.current, 'store');
      // Already checked into THIS store — don't duplicate the leg.
      if (openStore && openStore.storeId === storeId) {
        console.log('[useVisitDay] Already checked into store', storeId, '- skipping duplicate');
        if (projectId && openStore.projectId !== projectId) {
          commit(
            legsRef.current.map((l) => (l.id === openStore.id ? { ...l, projectId } : l))
          );
        }
        return undefined;
      }
      setIsLoading(true);
      const location = await tryLocation();
      const now = new Date();
      // Open the day BEFORE anything else touches the chain, so the window
      // encloses the legs this check-in is about to close.
      let clockLeg: TimeEntry | null = null;
      if (autoClockAt) {
        clockLeg = {
          id: generateLegId(),
          type: 'clock',
          startTime: autoClockAt,
          startLocation: location ?? undefined,
        };
        legsRef.current = [...legsRef.current, clockLeg];
      }
      const openDrive = openLegOfType(legsRef.current, 'drive');
      // Close the open drive AND any store leg left open at another store —
      // the rep can only be in one place, so a stale open store leg would
      // otherwise shadow this visit in every derived read.
      const closed = closeLegs(
        legsRef.current,
        (l) => l.type === 'drive' || l.type === 'store',
        now,
        location,
        odometerRef.current
      );
      // Derived from the CLOSED leg so the caller's check-in payload carries
      // the odometer pair the leg just captured.
      const closedDriveData = closedDriveDataFrom(closed.find((l) => l.id === openDrive?.id), now);
      commit(
        [
          ...closed,
          {
            id: generateLegId(),
            type: 'store',
            storeId,
            storeName,
            projectId,
            startTime: now,
            startLocation: location ?? undefined,
          },
        ],
        location
      );
      sendTelemetry('store_checkin', location, storeId);
      setIsLoading(false);
      // Arriving ends the drive, and the ledger has to hear about it (#3519).
      // Leaving a store emits drive_started and only this call ever closes that
      // drive, so without this the event stream carried drives that began and
      // never ended — anything pairing start to end had to special-case it, and
      // reconstruct_active_day replays a rep who is still driving at a store
      // they are standing in.
      if (closedDriveData) {
        void emitEvent({
          event_type: 'drive_ended',
          event_at: now,
          store_id: storeId,
          payload: {
            lat: location?.lat ?? null,
            lon: location?.lng ?? null,
            duration_minutes: closedDriveData.durationMinutes,
            start_odometer: closedDriveData.startOdometer ?? null,
            end_odometer: closedDriveData.endOdometer ?? null,
            ended_by: 'store_check_in',
          },
        });
      }
      // The auto-punch is a real clock-in and has to reach the ledger like any
      // other, or the Manager board and a fresh-device rehydrate both go on
      // believing the rep never started. `auto` marks it as the app's
      // assertion rather than the rep's, so a reader can tell them apart.
      if (clockLeg) {
        void emitEvent({
          event_type: 'day_started',
          event_at: clockLeg.startTime,
          store_id: storeId,
          payload: {
            retroactive: clockLeg.startTime.getTime() !== now.getTime(),
            auto: true,
            reason: 'store_check_in_without_clock_in',
            lat: location?.lat ?? null,
            lon: location?.lng ?? null,
          },
        }).then((rec) => {
          if (rec && clockLeg) stampEventId(clockLeg.id, 'startEventId', rec.client_event_id);
        });
      }
      return closedDriveData;
    },
    [commit, tryLocation, stampEventId]
  );

  const checkOutOfStore = useCallback(async () => {
    const openStore = openLegOfType(legsRef.current, 'store');
    if (!hasOpenClock(legsRef.current) || !openStore) return;
    setIsLoading(true);
    const location = await tryLocation();
    const now = new Date();
    const closed = closeLegs(legsRef.current, (l) => l.type === 'store', now, location, odometerRef.current);
    commit(
      [
        ...closed,
        {
          id: generateLegId(),
          type: 'drive',
          startTime: now,
          startLocation: location ?? undefined,
          // The vehicle hasn't moved since the rep arrived, so the reading
          // taken at arrival is where this drive begins.
          startOdometer: odometerRef.current ?? undefined,
        },
      ],
      location
    );
    sendTelemetry('store_checkout', location, openStore.storeId ?? undefined);
    // Leaving a store opens the next drive leg, so the ledger needs the same
    // drive_started it gets from startDriving — without it the derivation sees
    // a drive that ends without ever having begun.
    void emitEvent({
      event_type: 'drive_started',
      event_at: now,
      store_id: openStore.storeId ?? null,
      payload: {
        lat: location?.lat ?? null,
        lon: location?.lng ?? null,
        after_store_checkout: true,
      },
    });
    setIsLoading(false);
  }, [commit, tryLocation]);

  const cancelStoreVisit = useCallback(
    (storeId: number) => {
      commit(
        legsRef.current.filter((l) => !(l.type === 'store' && l.storeId === storeId && !l.endTime))
      );
    },
    [commit]
  );

  const resetDay = useCallback(() => {
    void clearStore(STORES.VISIT_DAY_SESSIONS);
    dayKeyRef.current = null;
    legsRef.current = [];
    locationRef.current = null;
    setLegs([]);
    setCurrentLocation(null);
  }, []);


  /**
   * Fix a clock punch after the fact — the rep clocked in late, forgot to
   * clock out, or never clocked in at all (#3452 forgot-punch recovery).
   *
   * The leg is corrected locally and a replacement punch is emitted naming the
   * one it supersedes, so the ledger derives from the corrected time instead
   * of seeing two conflicting punches. `at = null` on the out-punch reopens a
   * window the rep closed by mistake.
   */
  const correctClockPunch = useCallback(
    (legId: string, which: 'in' | 'out', at: Date | null) => {
      const target = legsRef.current.find((l) => l.id === legId && l.type === 'clock');
      if (!target) return;
      if (which === 'in' && !at) return; // a window must have a start
      // Reopening while another window is open would leave two running at
      // once, and the day's punches could then be attributed to the wrong one.
      if (which === 'out' && !at && hasOpenClock(legsRef.current)) return;

      const supersedes = which === 'in' ? target.startEventId : target.endEventId;
      const next = legsRef.current.map((l) => {
        if (l.id !== legId) return l;
        const updated: TimeEntry = { ...l, manual: true };
        if (which === 'in') {
          updated.startTime = at as Date;
        } else if (at) {
          updated.endTime = at;
        } else {
          delete updated.endTime;
          delete updated.endEventId;
        }
        updated.durationMinutes = updated.endTime
          ? spanMinutes(updated.startTime, updated.endTime)
          : undefined;
        return updated;
      });
      commit(next);

      if (!at) return; // reopening removes a punch; nothing new to record
      void emitEvent({
        event_type: which === 'in' ? 'day_started' : 'day_ended',
        event_at: at,
        correction_of_client_event_id: supersedes ?? null,
        payload: { corrected: true, retroactive: true },
      }).then((rec) => {
        if (rec) stampEventId(legId, which === 'in' ? 'startEventId' : 'endEventId', rec.client_event_id);
      });
      void emitEvent({
        event_type: 'time_edited',
        event_at: new Date(),
        payload: {
          field: which === 'in' ? 'clock_in' : 'clock_out',
          new_value: at.toISOString(),
          leg_id: legId,
        },
      });
    },
    [commit, stampEventId]
  );

  /**
   * Add a clock window the rep never punched — they worked a stretch with the
   * app closed and are entering it afterwards.
   */
  const addClockWindow = useCallback(
    (start: Date, end: Date | null) => {
      // An open-ended window while one is already running would leave two
      // clocks going; the rep should close the live one first.
      if (!end && hasOpenClock(legsRef.current)) return;
      const legId = generateLegId();
      const window: TimeEntry = {
        id: legId,
        type: 'clock',
        startTime: start,
        manual: true,
        ...(end ? { endTime: end, durationMinutes: spanMinutes(start, end) } : {}),
      };
      commit([...legsRef.current, window]);
      void emitEvent({
        event_type: 'day_started',
        event_at: start,
        payload: { retroactive: true, corrected: true },
      }).then((rec) => {
        if (rec) stampEventId(legId, 'startEventId', rec.client_event_id);
      });
      if (end) {
        void emitEvent({
          event_type: 'day_ended',
          event_at: end,
          payload: { retroactive: true, corrected: true },
        }).then((rec) => {
          if (rec) stampEventId(legId, 'endEventId', rec.client_event_id);
        });
      }
    },
    [commit, stampEventId]
  );

  const updateTimeEntry = useCallback(
    (
      entryId: string,
      updates: { startTime?: Date; endTime?: Date; storeName?: string; startOdometer?: number; endOdometer?: number }
    ) => {
      commit(
        legsRef.current.map((leg) => {
          if (leg.id !== entryId) return leg;
          const updated = { ...leg, ...updates };
          if (updated.startTime && updated.endTime) {
            updated.durationMinutes = spanMinutes(updated.startTime, updated.endTime);
          }
          return updated;
        })
      );
    },
    [commit]
  );

  const addManualEntry = useCallback(
    (
      type: 'drive' | 'store',
      startTime: Date,
      endTime: Date,
      storeName?: string,
      storeId?: number,
      startOdometer?: number,
      endOdometer?: number
    ) => {
      commit([
        ...legsRef.current,
        {
          id: generateLegId(),
          type,
          storeName,
          storeId,
          startTime,
          endTime,
          durationMinutes: spanMinutes(startTime, endTime),
          startOdometer,
          endOdometer,
          manual: true,
        },
      ]);
    },
    [commit]
  );

  /**
   * Take a manual entry into the day chain (#3519).
   *
   * The manual path used to call `stopDriving()`, which closes the drive at
   * `new Date()`. So a rep who arrived at 09:15 and filed at 09:40 wrote 09:15
   * to the CallLog, 09:40 to their own chain, and a `drive_ended` at 09:40 to
   * the ledger — one arrival, three answers. It also created no store leg at
   * all, so the visit was missing from the day's own totals until a refetch.
   *
   * Everything here is stamped at the instant the rep ASSERTED, never at the
   * moment they happened to submit. Only today's chain is touched: a
   * back-dated entry belongs to a day this chain does not represent, and the
   * CallLog is the record for it.
   */
  const injectManualVisit = useCallback(
    async (visit: {
      storeId: number;
      storeName: string;
      projectId?: number;
      arrivedAt: Date;
      departedAt?: Date | null;
      driveStartOdometer?: number;
      driveEndOdometer?: number;
    }) => {
      const { arrivedAt } = visit;
      // Two different questions, and conflating them broke #3274.
      //
      // WHICH DAY the entry describes decides where its store leg belongs: a
      // back-dated visit belongs to a day this chain does not represent, and
      // the CallLog is the record for it.
      //
      // But adopting the visit is present tense either way. The rep stopped
      // driving to file it, so today's open drive ends NOW — even when what
      // they are recording happened yesterday.
      const isToday = dayKeyOf(arrivedAt) === dayKeyOf(new Date());
      const driveEndsAt = isToday ? arrivedAt : new Date();

      let next = legsRef.current;
      const openDrive = openLegOfType(next, 'drive');
      if (openDrive) {
        next = closeLegs(
          next,
          (l) => l.id === openDrive.id,
          driveEndsAt,
          null,
          isToday ? visit.driveEndOdometer ?? null : null,
        );
      }

      if (!isToday) {
        // Nothing else of this entry belongs to today. No store leg, and no
        // punch invented on the strength of a record about another day.
        commit(next);
        if (openDrive) {
          void emitEvent({
            event_type: 'drive_ended',
            event_at: driveEndsAt,
            payload: {
              duration_minutes: spanMinutes(openDrive.startTime, driveEndsAt),
              start_odometer: openDrive.startOdometer ?? null,
              end_odometer: null,
              ended_by: 'manual_entry_adopted',
            },
          });
        }
        return;
      }

      // The envelope follows the work: recording a visit on a day with no
      // clock coverage creates the coverage, nobody builds an empty envelope
      // first. Two constraints keep the ledger truthful:
      //   · Only when this time is genuinely uncovered. A punch onto a day
      //     whose ledger already closed lands MID-timeline, and the server's
      //     reconstruction (keyed on the LAST day_started) then splits the
      //     rep's real session into unpaired halves.
      //   · Recording FINISHED work closes its envelope. Left open, the day
      //     runs on past the recorded visit — the >12h forgotten-checkout
      //     class rebuilt by the repair flow itself.
      // The punch must also reach the ledger (the active-day endpoint reads
      // ONLY day events), skewed one second clear of the visit's own events:
      // same-instant events tie-break by server id, and losing that coin
      // toss sorts the visit before its own day and drops it from a
      // rehydrate.
      const coveredByWindow = clockLegs(next).some(
        (w) => w.startTime <= arrivedAt && (!w.endTime || w.endTime >= arrivedAt),
      );
      if (!hasOpenClock(next) && !coveredByWindow) {
        const windowStart = new Date(enclosingStartFor(next, arrivedAt).getTime() - 1000);
        const windowEnd = visit.departedAt ? new Date(visit.departedAt.getTime() + 1000) : null;
        const windowLegId = generateLegId();
        next = [
          ...next,
          {
            id: windowLegId,
            type: 'clock',
            startTime: windowStart,
            manual: true,
            ...(windowEnd ? { endTime: windowEnd, durationMinutes: spanMinutes(windowStart, windowEnd) } : {}),
          },
        ];
        void emitEvent({
          event_type: 'day_started',
          event_at: windowStart,
          payload: { retroactive: true, asserted: true, source: 'manual_visit_envelope' },
        }).then((rec) => {
          if (rec) stampEventId(windowLegId, 'startEventId', rec.client_event_id);
        });
        if (windowEnd) {
          void emitEvent({
            event_type: 'day_ended',
            event_at: windowEnd,
            payload: { retroactive: true, asserted: true, source: 'manual_visit_envelope' },
          }).then((rec) => {
            if (rec) stampEventId(windowLegId, 'endEventId', rec.client_event_id);
          });
        }
      }

      // Any store leg left open elsewhere closes at the arrival too — the rep
      // cannot be in two places, and a stale one shadows this visit.
      const staleStore = openLegOfType(next, 'store');
      if (staleStore) {
        next = closeLegs(next, (l) => l.id === staleStore.id, arrivedAt, null, null);
      }

      next = [
        ...next,
        {
          id: generateLegId(),
          type: 'store',
          storeId: visit.storeId,
          storeName: visit.storeName,
          projectId: visit.projectId,
          startTime: arrivedAt,
          endTime: visit.departedAt ?? undefined,
          durationMinutes: visit.departedAt ? spanMinutes(arrivedAt, visit.departedAt) : undefined,
          manual: true,
        },
      ];
      commit(next);

      if (openDrive) {
        void emitEvent({
          event_type: 'drive_ended',
          event_at: arrivedAt,
          store_id: visit.storeId,
          payload: {
            duration_minutes: spanMinutes(openDrive.startTime, arrivedAt),
            start_odometer: openDrive.startOdometer ?? null,
            end_odometer: visit.driveEndOdometer ?? null,
            asserted: true,
          },
        });
      }
      // Without this the visit never reaches the Manager board and cannot be
      // replayed onto a fresh device — the CallLog alone is invisible to both.
      void emitEvent({
        event_type: 'store_checked_in',
        event_at: arrivedAt,
        store_id: visit.storeId,
        project_id: visit.projectId ?? null,
        payload: { manual_entry: true, asserted: true },
      });
    },
    [commit, stampEventId]
  );

  /**
   * Carry a corrected visit time back onto the leg it belongs to (#3519).
   *
   * Editing a visit PATCHes the CallLog and, until now, nothing else: the op's
   * applyLocal touches the visit-session row but never the chain, so a rep who
   * fixed a 134-minute drive on the Time page watched the server record change
   * while their own totals, and the prefill the next manual entry reads, kept
   * the old value. One correction, two answers again.
   */
  const applyVisitTimeCorrection = useCallback(
    (
      callLogId: number,
      times: { startTime?: Date; endTime?: Date; driveStartTime?: Date; driveEndTime?: Date },
    ) => {
      if (!times.startTime && !times.endTime && !times.driveStartTime && !times.driveEndTime) return;

      // The inbound drive carries no callLogId of its own — nothing ever stamps
      // one on a drive leg. It is identified by the join the chain already
      // guarantees: checking in closes the drive and opens the store at the SAME
      // instant, so the drive whose end is this visit's start is this visit's
      // drive. Resolved against the pre-correction times, since the same edit
      // may be moving both.
      const storeLeg = legsRef.current.find((l) => l.type === 'store' && l.callLogId === callLogId);
      const inboundDrive =
        storeLeg && (times.driveStartTime || times.driveEndTime)
          ? legsRef.current.find(
              (l) => l.type === 'drive' && l.endTime?.getTime() === storeLeg.startTime.getTime(),
            )
          : undefined;

      // A drive being ATTACHED — the visit had none — has no leg to correct.
      // The gap it fills exists precisely because the chain is missing this
      // leg, so it must be created or the hole never clears and the Time page
      // keeps offering the same repair. Both ends required: a lone corrected
      // time with no leg to correct stays a no-op rather than inventing a
      // boundary.
      const createdDrive: TimeEntry | null =
        storeLeg && inboundDrive === undefined && times.driveStartTime && times.driveEndTime
          ? {
              id: generateLegId(),
              type: 'drive',
              startTime: times.driveStartTime,
              endTime: times.driveEndTime,
              durationMinutes: spanMinutes(times.driveStartTime, times.driveEndTime),
              manual: true,
            }
          : null;

      let changed = createdDrive !== null;
      const next = legsRef.current.map((leg) => {
        const isStore = leg.type === 'store' && leg.callLogId === callLogId;
        const isDrive = inboundDrive !== undefined && leg.id === inboundDrive.id;
        if (!isStore && !isDrive) return leg;

        const startTime = (isStore ? times.startTime : times.driveStartTime) ?? leg.startTime;
        const endTime = (isStore ? times.endTime : times.driveEndTime) ?? leg.endTime;
        if (startTime.getTime() === leg.startTime.getTime() && endTime?.getTime() === leg.endTime?.getTime()) {
          return leg;
        }
        changed = true;
        return {
          ...leg,
          startTime,
          endTime,
          durationMinutes: endTime ? spanMinutes(startTime, endTime) : leg.durationMinutes,
        };
      });
      if (changed) commit(createdDrive ? [...next, createdDrive] : next);
    },
    [commit]
  );

  const deleteTimeEntry = useCallback(
    (entryId: string) => {
      commit(legsRef.current.filter((l) => l.id !== entryId));
    },
    [commit]
  );

  /**
   * Minutes on the currently-open activity leg, clamped by
   * MAX_OPEN_LEG_MINUTES so a punch the rep forgot to close reads as "long",
   * not as a 20-hour drive (triage #2).
   */
  /**
   * Remove a leg the rep should never have created — a clock-in tapped by
   * mistake, or the drive it opened (#3516).
   *
   * Editing already exists for punches and visits; deleting did not, so a
   * mistaken clock-in left a session, and the drive it opened, with no way to
   * take either back. Pausing the drive CLOSES it rather than discarding it, so
   * "stop driving" produced exactly the record the rep was trying to be rid of.
   *
   * Refuses when the leg is load-bearing, because a silent orphan is worse than
   * a refusal: a clock window that still contains work, or a drive already
   * attached to a synced visit (its times live on that CallLog, and the visit's
   * own edit path owns them).
   */
  const deleteLeg = useCallback(
    (legId: string): { ok: true } | { ok: false; reason: string } => {
      const target = legsRef.current.find((l) => l.id === legId);
      if (!target) return { ok: false, reason: 'That entry is no longer here.' };

      if (target.type === 'clock') {
        const inWindow = activityLegs(legsRef.current).filter(
          (l) =>
            l.startTime >= target.startTime &&
            (!target.endTime || l.startTime <= target.endTime) &&
            (l.type !== 'drive' || l.callLogId != null),
        );
        if (inWindow.some((l) => l.type === 'store')) {
          return {
            ok: false,
            reason: 'This stretch has store visits in it. Delete or move those first.',
          };
        }
      }

      if (target.callLogId != null) {
        return {
          ok: false,
          reason: 'This drive is recorded on a visit. Edit it from that visit instead.',
        };
      }

      // Deleting a stretch takes the legs it opened with it. The drive a
      // clock-in starts is local until a check-in writes it onto a CallLog, and
      // it appears nowhere on the Time page — leaving it behind would strand an
      // invisible leg that still counts toward the day's drive minutes. Legs
      // already recorded on a visit are kept: that visit owns them.
      const cascade = new Set<string>([legId]);
      if (target.type === 'clock') {
        for (const l of activityLegs(legsRef.current)) {
          if (l.callLogId != null) continue;
          if (l.startTime < target.startTime) continue;
          if (target.endTime && l.startTime > target.endTime) continue;
          cascade.add(l.id);
        }
      }
      commit(legsRef.current.filter((l) => !cascade.has(l.id)));

      // The ledger keeps the removal, so a correction is never a silent erase.
      void emitEvent({
        event_type: 'time_edited',
        event_at: new Date(),
        payload: {
          field: target.type === 'clock' ? 'clock_window_deleted' : `${target.type}_leg_deleted`,
          leg_id: legId,
          also_removed: cascade.size - 1,
          start: target.startTime.toISOString(),
          end: target.endTime ? target.endTime.toISOString() : null,
        },
      });
      return { ok: true };
    },
    [commit],
  );

  const getElapsedMinutes = useCallback((): number => {
    if (dayData.status !== 'active') return 0;
    const open = dayData.timeEntries.find((e) => !e.endTime);
    if (!open) return 0;
    return elapsedMinutesFor(open);
  }, [dayData]);

  const formatTime = useCallback((minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  }, []);

  const setCurrentEntryCallLogId = useCallback(
    (callLogId: number) => {
      const openStore = openLegOfType(legsRef.current, 'store');
      if (!openStore) return;
      commit(
        legsRef.current.map((leg) =>
          leg.id === openStore.id
            ? // Also clear `voided` if previously set — this lets the caller
              // "rewind" the store leg to a non-voided CallLog after abandoning
              // a later multi-task task (e.g. abandon NEUTROGENA after finishing
              // SUNCARE → revert store leg to SUNCARE's callLogId so the
              // drive-back at end-of-day still has a valid target). #1507.
              { ...leg, callLogId, voided: false }
            : leg
        )
      );
    },
    [commit]
  );

  const getLastCompletedStoreEntry = useCallback((): TimeEntry | undefined => {
    // Scope to the clock window being closed. The drive-back belongs to THIS
    // session; without this a later clock-out re-patches the previous
    // session's CallLog with a drive-back spanning the gap between them
    // (#3452 — multi-session days make that a normal flow, not a freak case).
    const windows = clockLegs(legs);
    const currentWindowStart = windows.length ? windows[windows.length - 1].startTime : null;
    // Skip voided entries — their CallLog is dead server-side, so PATCHing
    // drive-back odo onto it is a write-to-nowhere. #1507.
    const completed = activityLegs(legs).filter(
      (e) =>
        e.type === 'store' &&
        e.endTime &&
        !e.voided &&
        (!currentWindowStart || e.endTime >= currentWindowStart),
    );
    if (!completed.length) return undefined;
    return completed.reduce((latest, entry) =>
      entry.endTime!.getTime() > latest.endTime!.getTime() ? entry : latest
    );
  }, [legs]);

  // Mark any store legs whose callLogId is in the given list as voided, so
  // getLastCompletedStoreEntry falls through to the prior leg when
  // patchReturnDrive runs at end-of-day. Call this after voiding a CallLog
  // server-side (e.g. from abandonCurrentTask). #1507.
  const markCallLogsVoided = useCallback(
    (callLogIds: number[]) => {
      if (!callLogIds.length) return;
      const voidSet = new Set(callLogIds);
      commit(
        legsRef.current.map((leg) =>
          leg.callLogId && voidSet.has(leg.callLogId) ? { ...leg, voided: true } : leg
        )
      );
    },
    [commit]
  );

  return {
    dayData,
    // The day's clock-in/out windows, for the Time page's punch corrections.
    clockWindows: useMemo(() => clockLegs(legs), [legs]),
    isLoading,
    error,
    correctClockPunch,
    addClockWindow,
    // `clockIn`/`clockOut` are the punches the rep sees; `startDay`/`endDay`
    // are the same functions under their original names, still used by the
    // retroactive-start paths in CallDetail / SetDetail / StoreDetail.
    clockIn: startDay,
    clockOut: endDay,
    startDay,
    endDay,
    startDriving,
    stopDriving,
    checkIntoStore,
    checkOutOfStore,
    cancelStoreVisit,
    resetDay,
    updateTimeEntry,
    addManualEntry,
    injectManualVisit,
    applyVisitTimeCorrection,
    deleteTimeEntry,
    deleteLeg,
    getElapsedMinutes,
    formatTime,
    setCurrentEntryCallLogId,
    getLastCompletedStoreEntry,
    markCallLogsVoided,
  };
}
